import { FC, forwardRef, useEffect, useState, FocusEvent, useRef } from 'react';

import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';

import { Menu, UnstyledButton, rem, useMantineColorScheme } from '@mantine/core';

import { NumberInput, NumberInputProps } from '../../inputs';

export type CurrencyUnit = 'cr' | 'l';

export interface CoverInputProps extends NumberInputProps {
  /** show Lakhs / L */
  expandUnit?: boolean;
  /**
   * default unit
   */
  unit?: 'l' | 'cr';
  /**
   * show error icon always in case of error
   */
  shouldShowErrorIcon?: boolean;
}

const ONE_CRORE = 10000000;
const ONE_LAKH = 100000;

const CoverInput: FC<CoverInputProps> = forwardRef<
  HTMLInputElement,
  CoverInputProps
>(
  (
    {
      value,
      min = 0,
      max,
      disabled,
      decimalScale,
      expandUnit,
      unit,
      onChange,
      shouldShowErrorIcon = true,
      // important: if you set this to false, the values are rounded by default. 2.3 => 2, 2.6 => 3
      clampBehavior = 'none',
      ...rest
    },
    ref
  ) => {
    const scheme = useMantineColorScheme();
    /**
     * function to calculate the initial value based on incoming value
     * @param min
     * @returns
     */
    const initVal = (min?: number) => {
      const convertVal = min ? min : (value as number);
      const init = convertVal
        ? convertVal >= ONE_CRORE
          ? convertVal / ONE_CRORE
          : convertVal >= ONE_LAKH
          ? convertVal / ONE_LAKH
          : convertVal
        : undefined;

      return init;
    };

    const [localVal, setLocalVal] = useState<number | undefined>(initVal());
    const localValRef = useRef(localVal);
    const [localUnit, setLocalUnit] = useState<CurrencyUnit>(
      unit ? unit : value ? ((value as number) >= ONE_CRORE ? 'cr' : 'l') : 'l'
    );
    /**
     * Clicking on unit shows a menu this state var keeps track of that
     */
    const [opened, setOpened] = useState(false);

    /**
     * when ever value changes update the localVal and and localUnit
     */
    useEffect(() => {
      if (value) {
        // if there are no changes in current value shown in the input don't change anything
        if (
          !localValRef.current ||
          value !== localValRef.current * (localUnit === 'l' ? ONE_LAKH : ONE_CRORE)
        ) {
          const defaultVal = initVal(value as number);
          setLocalVal(defaultVal);
          localValRef.current = defaultVal;
          setLocalUnit(value && (value as number) >= ONE_CRORE ? 'cr' : 'l');
        }
      } else {
        setLocalVal(undefined);
      }
    }, [value]);

    const handleOnChange = (value: number | undefined, currUnit: CurrencyUnit) => {
      if (value || value === 0) {
        setLocalVal(value);
        localValRef.current = value;
        onChange && onChange(value * (currUnit === 'l' ? ONE_LAKH : ONE_CRORE));
      }
    };

    /**
     * when user changes the unit update the current value in state perform calculations
     * to keep values in sync
     *
     * @param currUnit
     */
    const handleUnitClick = (
      newUnit: CurrencyUnit,
      currUnit: CurrencyUnit,
      currVal: number | undefined
    ) => {
      if (newUnit !== currUnit) {
        const valueWithNewUnit = currVal
          ? currVal * (newUnit === 'l' ? ONE_LAKH : ONE_CRORE)
          : 0;

        let finalValueWithUnit = valueWithNewUnit;
        if (max && valueWithNewUnit > max) {
          finalValueWithUnit = max;
        }
        if (min && valueWithNewUnit < min) {
          finalValueWithUnit = min;
        }

        if (finalValueWithUnit >= ONE_CRORE) {
          setLocalUnit('cr');
          setLocalVal(finalValueWithUnit / ONE_CRORE);
          onChange && onChange(finalValueWithUnit);
          localValRef.current = finalValueWithUnit / ONE_CRORE;
        } else {
          setLocalVal(finalValueWithUnit / ONE_LAKH);
          setLocalUnit(newUnit);
          onChange && onChange(finalValueWithUnit);
          localValRef.current = finalValueWithUnit / ONE_LAKH;
        }
      }
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        /**
         * if min was set, set localVal and Unit based on that
         * Otherwise trigger onChange with undefined
         */
        if (min) {
          const defaultVal = initVal(min);
          setLocalVal(defaultVal);
          setLocalUnit(defaultVal && defaultVal >= ONE_CRORE ? 'cr' : 'l');
          onChange && onChange(min);
        } else {
          onChange && onChange('');
        }
      } else {
        const targetVal = parseFloat(event.target.value);

        const valueWithUnit = targetVal * (localUnit === 'l' ? ONE_LAKH : ONE_CRORE);
        let finalValueWithUnit = valueWithUnit;
        if (max && valueWithUnit > max) {
          finalValueWithUnit = max;
        }
        if (min && valueWithUnit < min) {
          finalValueWithUnit = min;
        }
        if (localUnit === 'l') {
          if (finalValueWithUnit >= ONE_CRORE) {
            setLocalVal(finalValueWithUnit / ONE_CRORE);
            setLocalUnit('cr');
          } else {
            setLocalVal(finalValueWithUnit / ONE_LAKH);
          }
        } else {
          setLocalVal(finalValueWithUnit / ONE_CRORE);
        }
        localValRef.current = finalValueWithUnit;
        onChange && onChange(finalValueWithUnit);
      }
    };

    return (
      <NumberInput
        styles={{
          input: {
            paddingRight: !expandUnit ? rem(44) : rem(58)
          }
        }}
        {...rest}
        ref={ref}
        clampBehavior={clampBehavior}
        decimalSeparator="."
        disabled={disabled}
        decimalScale={decimalScale}
        hideControls
        value={localVal}
        onChange={(value) => {
          handleOnChange(value as number, localUnit);
        }}
        onBlur={handleBlur}
        shouldShowErrorIcon={shouldShowErrorIcon}
        leftSection={<>₹</>}
        rightSection={
          <Menu
            shadow="md"
            width={100}
            opened={opened}
            onChange={setOpened}
            closeOnItemClick
          >
            <Menu.Target>
              {
                <UnstyledButton
                  style={(theme) => ({
                    fontSize: 13,
                    color:
                      scheme.colorScheme === 'light'
                        ? theme.colors.text[4]
                        : undefined,
                    marginRight: !expandUnit ? 24 : 40,
                    display: 'flex',
                    alignItems: 'center'
                  })}
                  disabled={disabled}
                >
                  {localUnit === 'cr'
                    ? expandUnit
                      ? 'Crores'
                      : 'Cr'
                    : expandUnit
                    ? 'Lakhs'
                    : 'L'}{' '}
                  {!opened ? <RiArrowDownSLine /> : <RiArrowUpSLine />}
                </UnstyledButton>
              }
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleUnitClick('cr', localUnit, localVal)}>
                Crore
              </Menu.Item>
              <Menu.Item onClick={() => handleUnitClick('l', localUnit, localVal)}>
                Lakh
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        }
      />
    );
  }
);

export { CoverInput };
